<template>
  <div>
    <!-- Silme onay modali -->
    <DeleteConfirmModal
      v-if="showDeleteModal"
      @confirm="deleteJob"
      @cancel="showDeleteModal = false"
    />

    <section class="section">
      <!-- Belge Yükleme -->
      <div class="upload-box">
        <form @submit.prevent="submitFile">
          <h1 class="welcome">{{ $t('welcome') }}, {{ user?.name }}!</h1>
          <h3 class="subtitle">{{ $t('new_translation_request') }}</h3>

          <input v-model="description" type="text" :placeholder="$t('document_description')" required />

          <select v-model="sourceLang" required>
            <option value="" disabled>{{ $t('select_source_language') }}</option>
            <option v-for="lang in languages" :key="lang.id" :value="lang.name">{{ lang.name }}</option>
          </select>

          <select v-model="targetLang" required>
            <option value="" disabled>{{ $t('select_target_language') }}</option>
            <option v-for="lang in languages" :key="lang.id" :value="lang.name">{{ lang.name }}</option>
          </select>

          <div class="select-wrapper">
            <select v-model="translationType" required :class="{ placeholder: translationType === '' }">
              <option disabled value="">{{ $t('select_translation_type') }}</option>
              <option value="Apostil Tasdikli Çeviri">{{ $t('apostille_translation') }}</option>
              <option value="Resmi Belge Çeviri">{{ $t('official_document_translation') }}</option>
              <option value="Noter Tasdikli Çeviri">{{ $t('notary_translation') }}</option>
              <option value="Yeminli Çeviri">{{ $t('sworn_translation') }}</option>
            </select>
            <button type="button" class="info-icon" @click="openTranslationInfo" :disabled="!translationType">
              ?
            </button>
          </div>

          <select v-if="hatTurleri.length" v-model="hatTurId" required>
            <option disabled value="">{{ $t('select_calligraphy_type') }}</option>
            <option v-for="hat in hatTurleri" :key="hat.id" :value="hat.id">
              {{ hat.name }}
            </option>
          </select>

          <select v-model="specialization" required>
            <option disabled value="">{{ $t('select_specialization') }}</option>
            <option value="Genel">{{ $t('general') }}</option>
            <option value="Medikal">{{ $t('medical') }}</option>
            <option value="Akademik">{{ $t('academic') }}</option>
            <option value="Hukuki">{{ $t('legal') }}</option>
            <option value="Teknik">{{ $t('technical') }}</option>
          </select>

          <label class="mt-0 mb-0"><p>{{ $t('delivery_date_label') }}</p></label>
          <input v-model="deliveryDate" type="date" :min="today" required />

          <div class="file-row">
            <input ref="fileInput" type="file" required />
            <button type="button" class="btn red" @click="clearFile">{{ $t('delete') }}</button>
          </div>

          <button type="submit" class="btn">{{ $t('upload_document') }}</button>
        </form>
      </div>

      <!-- Mevcut İşler -->
      <div class="jobs-box">
        <h3>{{ $t('your_jobs') }}</h3>

        <div class="tab-buttons">
          <button :class="{ active: currentTab === 'active' }" @click="currentTab = 'active'">
            {{ $t('active') }}
          </button>
          <button :class="{ active: currentTab === 'completed' }" @click="currentTab = 'completed'">
            {{ $t('completed') }}
          </button>
        </div>

        <div class="table-wrapper">
          <table class="jobs-table">
            <thead>
              <tr>
                <th class="sortable" @click="sortBy('id')">
                  <div class="th-content">
                    <span>{{ $t('job_code') }}</span>
                    <span v-if="sortKey === 'id'" class="sort-icon">{{ sortDirection === 1 ? '▲' : '▼' }}</span>
                  </div>
                </th>
                <th class="sortable" @click="sortBy('file_name')">
                  <div class="th-content">
                    <span>{{ $t('file') }}</span>
                    <span v-if="sortKey === 'file_name'" class="sort-icon">{{ sortDirection === 1 ? '▲' : '▼' }}</span>
                  </div>
                </th>
                <th class="sortable" @click="sortBy('status')">
                  <div class="th-content">
                    <span>{{ $t('status') }}</span>
                    <span v-if="sortKey === 'status'" class="sort-icon">{{ sortDirection === 1 ? '▲' : '▼' }}</span>
                  </div>
                </th>
                <th class="sortable" @click="sortBy('specialization')">
                  <div class="th-content">
                    <span>{{ $t('specialization') }}</span>
                    <span v-if="sortKey === 'specialization'" class="sort-icon">{{ sortDirection === 1 ? '▲' : '▼' }}</span>
                  </div>
                </th>
                <th class="sortable" @click="sortBy('translator_name')">
                  <div class="th-content">
                    <span>{{ $t('translator') }}</span>
                    <span v-if="sortKey === 'translator_name'" class="sort-icon">{{ sortDirection === 1 ? '▲' : '▼' }}</span>
                  </div>
                </th>
                <th class="sortable" @click="sortBy('delivery_date')">
                  <div class="th-content">
                    <span>{{ $t('delivery') }}</span>
                    <span v-if="sortKey === 'delivery_date'" class="sort-icon">{{ sortDirection === 1 ? '▲' : '▼' }}</span>
                  </div>
                </th>
                <th class="sortable" @click="sortBy('admin_price')">
                  <div class="th-content">
                    <span>{{ $t('price') }}</span>
                    <span v-if="sortKey === 'admin_price'" class="sort-icon">{{ sortDirection === 1 ? '▲' : '▼' }}</span>
                  </div>
                </th>
                <th v-if="currentTab === 'completed'">{{ $t('outputs') }}</th>
                <th class="sortable" @click="sortBy('is_paid')">
                  <div class="th-content">
                    <span>{{ $t('payment') }}</span>
                    <span v-if="sortKey === 'is_paid'" class="sort-icon">{{ sortDirection === 1 ? '▲' : '▼' }}</span>
                  </div>
                </th>
                <th>{{ currentTab === 'completed' ? $t('contact') : $t('cancel') }}</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="job in paginatedJobs" :key="job.id">
                <td data-label="İş Kodu">JOB-{{ String(job.id).padStart(6, '0') }}</td>
                <td data-label="Dosya">{{ getOriginalFileName(job.file_name) }}</td>
                <td data-label="Durum">
                  <span :class="['status', getStatusClass(job.status)]">{{ formatStatus(job.status) }}</span>
                </td>
                <td data-label="Uzmanlık">{{ job.specialization || '-' }}</td>
                <td data-label="Tercüman">{{ job.translator_name || '-' }}</td>
                <td data-label="Teslim">{{ formatDate(job.delivery_date) }}</td>
                <td data-label="Fiyat">
                  <span v-if="job.admin_price">₺{{ job.admin_price }}</span>
                  <span v-else>-</span>
                </td>

                <td v-if="currentTab === 'completed'" data-label="Çıktılar">
                  <div v-if="job.output_files?.length" class="outputs">
                    <div v-for="file in job.output_files" :key="file" class="output-item">
                      <a
                        :href="`${baseURL}/uploads/output/${encodeURIComponent(file)}`"
                        target="_blank"
                        download
                        class="btn small"
                      >
                        {{ file }}
                      </a>
                    </div>
                  </div>
                  <span v-else>-</span>
                </td>

                <td data-label="Ödeme">
                  <a
                    class="btn"
                    :href="generateWhatsAppLink(job)"
                    target="_blank"
                    v-if="job.status === 'waiting_payment' && !job.is_paid"
                  >
                    ₺{{ job.admin_price }} {{ $t('pay_now') }}
                  </a>
                  <span v-else-if="job.is_paid">✅ {{ $t('paid') }}</span>
                  <span v-else>-</span>
                </td>

                <td v-if="currentTab === 'active'" data-label="İptal">
                  <button
                    v-if="job.status === 'waiting' || job.status === 'quoted'"
                    @click="confirmDelete(job.id)"
                    class="btn red"
                  >
                    {{ $t('cancel') }}
                  </button>
                  <span v-else>-</span>
                </td>
                <td v-else data-label="İletişim">
                  <a class="btn" :href="generateWhatsAppLink(job)" target="_blank">
                    {{ $t('request_revision') }}
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Sayfalama -->
        <div class="pagination">
          <button @click="currentPage--" :disabled="currentPage === 1">‹</button>
          <span>{{ currentPage }} / {{ totalPages }}</span>
          <button @click="currentPage++" :disabled="currentPage === totalPages">›</button>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { getToken, getUser } from '@/utils/auth'
import DeleteConfirmModal from '@/components/DeleteConfirmModal.vue'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
const baseURL = process.env.VUE_APP_API_URL
const router = useRouter()
const user = getUser()
if (user?.role !== 'user') {
  alert('Bu sayfaya sadece kullanıcılar erişebilir.')
  router.push('/') // veya login sayfası
}

const jobs = ref([])
const languages = ref([])
const specialization = ref('')
const description = ref('')
const fileInput = ref(null)
const sourceLang = ref('')
const targetLang = ref('')
const deliveryDate = ref('')
const translationType = ref('')
const hatTurleri = ref([])       // Tüm hat türleri listesi
const hatTurId = ref('')         // Seçilen hat türü ID'si
const today = new Date().toISOString().split('T')[0]
//delete modal
const showDeleteModal = ref(false)
const selectedJobId = ref(null)

// Sekme ve sayfa yönetimi
const currentTab = ref('active') // 'active' | 'completed'
const currentPage = ref(1)
const pageSize = 5
const sortKey = ref('')
const sortDirection = ref(1) // 1 = ASC, -1 = DESC

watch(currentTab, () => {
  currentPage.value = 1
})

// Filtrelenmiş işler
const filteredJobs = computed(() => {
  if (currentTab.value === 'completed') {
    return jobs.value.filter(j => j.status === 'completed')
  }
  return jobs.value.filter(j => j.status !== 'completed')
})

// Sayfalama
const paginatedJobs = computed(() => {
  const start = (currentPage.value - 1) * pageSize
  return sortedJobs.value.slice(start, start + pageSize)
})
const totalPages = computed(() => Math.ceil(filteredJobs.value.length / pageSize))

// API istekleri
async function fetchJobs() {
  const res = await fetch(`${baseURL}/user/jobs`, {
    headers: { Authorization: `Bearer ${getToken()}` }
  })

  const data = await res.json()

  // Eğer dizi geliyorsa direkt ata
  if (Array.isArray(data)) {
    jobs.value = data
  }
  // Eğer { jobs: [...] } gibi geliyorsa
  else if (Array.isArray(data.jobs)) {
    jobs.value = data.jobs
  }
  // Eğer beklenmeyen bir şey geldiyse boş dizi yap
  else {
    console.warn('Beklenmeyen veri formatı:', data)
    jobs.value = []
  }
}

async function fetchLanguages() {
  const res = await fetch(`${baseURL}/user/languages`, {
    headers: { Authorization: `Bearer ${getToken()}` }
  })
  if (res.ok) {
    languages.value = await res.json()
  }
}

async function submitFile() {
  const file = fileInput.value?.files[0]
  if (!file || !description.value || !sourceLang.value || !targetLang.value || !deliveryDate.value) {
    return alert('Tüm alanları doldurun.')
  }

const formData = new FormData()
formData.append('file', file)
formData.append('description', description.value)
formData.append('source_lang', sourceLang.value)
formData.append('target_lang', targetLang.value)
formData.append('delivery_date', deliveryDate.value)
formData.append('translation_type', translationType.value)
formData.append('specialization', specialization.value)

// Sadece Osmanlıca ise gönder
if (sourceLang.value === 'Osmanlıca') {
    formData.append('hat_tur_id', hatTurId.value || '')
}

  const res = await fetch(`${baseURL}/user/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}` },
    body: formData
  })

  if (res.ok) {
    alert('Yüklendi!')
    resetForm()
    fetchJobs()
  } else {
    alert('Hata: ' + await res.text())
  }
}



function resetForm() {
  specialization.value = ''
  description.value = ''
  sourceLang.value = ''
  targetLang.value = ''
    deliveryDate.value = ''
    hatTurId.value = ''
    hatTurleri.value = []
  if (fileInput.value) fileInput.value.value = ''
}
function clearFile() {
  if (fileInput.value) fileInput.value.value = ''
}

function goToPayment(jobId) {
  router.push(`/odeme/${jobId}`)
}

function getStatusClass(status) {
  return {
    waiting: 'waiting',
    in_progress: 'inprogress',
    completed: 'done',
    price_sent: 'quoted',
    waiting_payment: 'quoted'
  }[status] || ''
}

function formatStatus(status) {
  return {
    waiting: 'Bekliyor',
    in_progress: 'Çeviride',
    completed: 'Tamamlandı',
    price_sent: 'Fiyat Bekliyor',
    waiting_payment: 'Ödeme Bekliyor'
  }[status] || status
}

function formatDate(date) {
  if (!date) return '-'
  const d = new Date(date)
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  return `${day}.${month}.${year}`
}

function getOriginalFileName(fileName) {
  if (!fileName) return '-'
  // İlk tireye kadar olan kısmı at, kalanı göster
  return fileName.substring(fileName.indexOf('-') + 1)
}

function sortBy(key) {
  if (sortKey.value === key) {
    sortDirection.value *= -1
  } else {
    sortKey.value = key
    sortDirection.value = 1
  }
}
function getSortIcon(key) {
  if (sortKey.value !== key) return ''
  return sortDirection.value === 1 ? '▲' : '▼'
}
function confirmDelete(id) {
  selectedJobId.value = id
  showDeleteModal.value = true
}

function deleteJob() {
  if (!selectedJobId.value) return
  fetch(`${baseURL}/user/job/${selectedJobId.value}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getToken()}` }
  }).then(() => {
    showDeleteModal.value = false
    selectedJobId.value = null // ✅ ekle
    fetchJobs()
  }).catch(() => {
    alert("Silme başarısız.")
    showDeleteModal.value = false
    selectedJobId.value = null // ✅ hata anında da sıfırla
  })
}

const sortedJobs = computed(() => {
  const list = [...filteredJobs.value]
  return list.sort((a, b) => {
    let valA = a[sortKey.value]
    let valB = b[sortKey.value]

    if (sortKey.value === 'id') {
      valA = Number(valA)
      valB = Number(valB)
    }

    if (sortKey.value === 'delivery_date') {
      valA = new Date(valA)
      valB = new Date(valB)
    }

    if (sortKey.value === 'admin_price') {
      valA = Number(valA || 0)
      valB = Number(valB || 0)
    }
    if (sortKey.value === 'specialization') {
      valA = valA?.toLowerCase?.() || ''
      valB = valB?.toLowerCase?.() || ''
    }

    if (valA < valB) return -1 * sortDirection.value
    if (valA > valB) return 1 * sortDirection.value
    return 0
  })
})

function openTranslationInfo() {
    const pages = {
        "Apostil Tasdikli Çeviri": "/services/apostil",
        "Resmi Belge Çeviri": "/services/resmi",
        "Noter Tasdikli Çeviri": "/services/noter",
        "Yeminli Çeviri": "services/yeminli"
    }

    const selected = translationType.value
    if (pages[selected]) {
        window.open(pages[selected], '_blank')
    } else {
        alert("Sayfa bulunamadı.")
    }
}



function generateWhatsAppLink(job) {
  const isCompleted = job.status === 'completed'
  const message = `
${isCompleted ? '✅ TAMAMLANAN İŞ HAKKINDA İLETİŞİME GEÇİLDİ:' : '📌 YENİ İŞ TALEBİ:'}

📌 İş Kodu: JOB-${job.id}
👤 Müşteri: ${user.name} ${user.surname || ''}
📄 Dosya: ${getOriginalFileName(job.file_name)}
🌐 Diller: ${job.source_lang || ''} → ${job.target_lang || ''}
📅 Teslim Tarihi: ${formatDate(job.delivery_date)}
💰 Fiyat: ₺${job.admin_price || '-'}

${isCompleted ? 'Revizyon işin iletişime geçiyorum.' : 'Bilgi almak istiyorum.'}
  `.trim()

  return `https://wa.me/905321670115?text=${encodeURIComponent(message)}`
}

function generateWhatsAppLink2(job) {
  const message = `
✅ TAMAMLANAN BİR İŞ HAKKINDA İLETİŞİME GEÇİLDİ:

📌 İş Kodu: JOB-${job.id}
👤 Müşteri: ${user.name} ${user.surname || ''}
📄 Dosya: ${getOriginalFileName(job.file_name)}
🌐 Diller: ${job.source_lang || ''} → ${job.target_lang || ''}
📅 Teslim Tarihi: ${formatDate(job.delivery_date)}
💰 Fiyat: ₺${job.admin_price || '-'}

Yeni bir talep oluşturmak istiyorum.
  `.trim()

  return `https://wa.me/905321670115?text=${encodeURIComponent(message)}`
}

onMounted(() => {
  fetchJobs()
  fetchLanguages()
})

watch(sourceLang, async (newLang) => {
    const osmanlica = languages.value.find(l => l.name === 'Osmanlıca')
    if (newLang === 'Osmanlıca' && osmanlica) {
        const res = await fetch(`${baseURL}/user/hat-turleri/${osmanlica.id}`, {
            headers: { Authorization: `Bearer ${getToken()}` }
        })
        if (res.ok) {
            hatTurleri.value = await res.json()
        }
    } else {
        hatTurleri.value = []
        hatTurId.value = ''
    }
})
</script>

<style scoped>
/* ==== Global Styles ==== */
.section {
  padding: 30px;
  max-width: 1100px;
  margin: auto;
  padding-top: 120px;
}
/* ==== Table Ok İşaretleri ==== */

.th-content {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.sort-icon {
  font-size: 0.85em;
  color: #555;
}

/* ==== Upload Box ==== */
.upload-box {
  background: #f9f9f9;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  margin: 100px auto 40px;
  max-width: 600px;
  width: 100%;
}

.upload-box form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.upload-box input,
.upload-box select {
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #ccc;
  font-size: 15px;
}

.upload-box h1.welcome {
  text-align: center;
  font-size: 22px;
  margin-bottom: 10px;
  color: #333;
}

.upload-box h3.subtitle {
  text-align: center;
  font-size: 18px;
  margin-bottom: 20px;
  color: #555;
}

.file-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* ==== Jobs Box ==== */
.jobs-box {
  background: #f9f9f9;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  margin-bottom: 40px;
  max-width: 1100px;
  margin-left: auto;
  margin-right: auto;
  overflow-x: auto;
}

.jobs-box h3 {
  font-size: 20px;
  margin-bottom: 20px;
  text-align: center;
}

/* ==== Tab Buttons ==== */
.tab-buttons {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 20px;
}

.tab-buttons button {
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  background-color: #eee;
  cursor: pointer;
  font-weight: bold;
  color: #333;
  transition: background-color 0.3s ease;
}

.tab-buttons .active {
  background-color: #3498db;
  color: white;
}

/* ==== Table ==== */
.table-wrapper {
  min-height: 500px;
  overflow-x: auto;
}

.jobs-table {
  width: 100%;
  min-width: unset;
  table-layout: auto;
}

.jobs-table th,
.jobs-table td {
  padding: 12px;
  border-bottom: 1px solid #ddd;
  text-align: left;
  vertical-align: top;
  white-space: nowrap; /* taşmayı önler */
}

.jobs-table th.sortable {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.3s ease;
}

.jobs-table th.sortable:hover {
  background-color: #f0f4fa;
}

/* ==== Status ==== */
.status {
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.85em;
  font-weight: 500;
  display: inline-block;
}

.status.waiting {
  background-color: #ffeaa7;
  color: #c57f17;
}

.status.inprogress {
  background-color: #a29bfe;
  color: #2d3436;
}

.status.done,
.status.completed {
  background-color: #55efc4;
  color: #00695c;
}

/* ==== Output File Button ==== */
.outputs {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.output-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ==== Buttons ==== */
.btn {
  background-color: #3498db;
  color: white;
  padding: 10px;
  border: none;
  border-radius: 6px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.btn:hover {
  background-color: #2980b9;
}

.btn.red {
  background-color: #e74c3c;
}

.btn.red:hover {
  background-color: #c0392b;
}

.btn.small {
  padding: 4px 8px;
  font-size: 0.85em;
}

/* ==== Pagination ==== */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 25px;
}

.pagination button {
  padding: 6px 12px;
  border-radius: 6px;
  border: none;
  font-weight: bold;
  cursor: pointer;
  background-color: #3498db;
  color: white;
  transition: background-color 0.3s ease;
}

.pagination button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.pagination span {
  font-weight: bold;
  color: #333;
}

select option[disabled] {
    color: #aaa;
}

.select:invalid {
    color: #aaa;
}


.select-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.select-wrapper select {
    flex: 1;
    padding-right: 40px; /* ikon için yer bırak */
}

.select-wrapper .info-icon {
    position: absolute;
    right: 15px;
    background: #3498db;
    color: white;
    border: none;
    border-radius: 50%;
    width: 22px;
    height: 22px;
    font-weight: bold;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
}

.select-wrapper .info-icon:disabled {
    background-color: #ccc;
    cursor: not-allowed;
}
/* ==== Responsive ==== */
@media (max-width: 768px) {
  .jobs-box {
    padding: 15px;
  }

  .jobs-table,
  .jobs-table thead,
  .jobs-table tbody,
  .jobs-table th,
  .jobs-table td,
  .jobs-table tr {
    display: block;
    width: 100%;
  }

  .jobs-table thead {
    display: none;
  }

  .jobs-table tr {
    margin-bottom: 20px;
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 12px;
    background-color: #fff;
    box-shadow: 0 0 6px rgba(0, 0, 0, 0.05);
  }

  .jobs-table td {
    padding: 8px 10px;
    position: relative;
    text-align: left;
    word-break: break-word;
  }

  .jobs-table td::before {
    content: attr(data-label);
    font-weight: bold;
    display: block;
    margin-bottom: 6px;
    color: #555;
  }

  .tab-buttons {
    flex-direction: column;
    align-items: stretch;
  }

  .pagination {
    flex-direction: column;
    gap: 6px;
  }
}
@media (max-width: 768px) {
  .upload-box {
    margin: 50px 15px;
    padding: 20px;
  }

  .upload-box form {
    gap: 10px;
  }

  .upload-box input,
  .upload-box select {
    font-size: 14px;
  }
}
</style>
